import { ZonedDateTime } from '@js-joda/core';
import clsx from 'clsx';
import React from 'react';
import {
  type Key,
  SelectionIndicator,
  type SharedElementRenderProps,
  type StyleRenderProps,
  Tab,
  TabList,
  TabPanel,
  type TabRenderProps,
  Tabs,
} from 'react-aria-components';

import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';

import {
  DrawnSignatureOnChange,
  type DrawnSignatureProps,
  type SignatureInputOnChange,
  type SignatureType,
  type TypedSignatureOnChange,
  type TypedSignatureProps,
} from './types/SignatureInput';
import { SignatureInputDraw } from './SignatureInputDraw';
import { SignatureInputType } from './SignatureInputType';

type SignatureInputProps = {
  value?: string;
  signedAt?: ZonedDateTime;
  signatureType?: SignatureType;
  isError?: boolean;
  className?: string;
  onChange: SignatureInputOnChange;
};

const getTabClassName: StyleRenderProps<TabRenderProps>['className'] = ({ isSelected }: TabRenderProps) =>
  clsx('w-full position-relative', 'cursor-pointer', 'text-center font-600', isSelected && 'text-primary');

const getSelectionIndicatorClassName: StyleRenderProps<SharedElementRenderProps>['className'] = ({
  isEntering,
  isExiting,
}) =>
  clsx(
    'bg-primary',
    'position-absolute w-full h-[3px] bottom-0',
    isEntering && 'animate-fade-in',
    isExiting && 'animate-fade-out',
  );

export function SignatureInput({ className, value, signedAt, isError, onChange }: Readonly<SignatureInputProps>) {
  const { track } = useAnalytics();
  const timesheetId = useTimesheetStore(state => state.timesheet?.timesheetId ?? '');

  const drawTabId = React.useId();
  const typeTabId = React.useId();
  const [selectedTab, setSelectedTab] = React.useState<Key>(drawTabId);

  const [drawnSignature, setDrawnSignature] = React.useState<DrawnSignatureProps>({
    value,
    signedAt,
  });

  const [typedSignature, setTypedSignature] = React.useState<TypedSignatureProps>({
    value,
    signedAt,
    nameAsSignature: '',
  });

  const handleDrawnSignatureChange: DrawnSignatureOnChange = (newValue, newSignedAt) => {
    setDrawnSignature({ value: newValue, signedAt: newSignedAt });
    onChange(newValue, newSignedAt, 'drawn');
  };

  const handleTypedSignatureChange: TypedSignatureOnChange = (newValue, newSignedAt, newNameAsSignature) => {
    setTypedSignature({ value: newValue, signedAt: newSignedAt, nameAsSignature: newNameAsSignature });
    onChange(newValue, newSignedAt, 'typed');
  };

  // When switching tabs, apply the current signature value of the selected tab
  const handleTabSelect = (tabId: string) => {
    if (tabId === drawTabId) {
      onChange(drawnSignature.value, drawnSignature.signedAt, 'drawn');
      track('Online approval web - Press Draw Signature tab', { Timesheet_ID: timesheetId });
    } else {
      onChange(typedSignature.value, typedSignature.signedAt, 'typed');
      track('Online approval web - Press Type Signature tab', { Timesheet_ID: timesheetId });
    }
  };

  return (
    <Tabs
      className={clsx(
        'flex flex-col w-full',
        'rounded-sm border-1',
        isError ? 'border-error' : 'border-outline-l4',
        'bg-surface-l0',
        className,
      )}
      selectedKey={selectedTab}
      onSelectionChange={setSelectedTab}
    >
      <TabList
        aria-label="Signature input options"
        className={clsx('flex justify-center', 'border-b border-outline-l4')}
      >
        <Tab id={drawTabId} className={getTabClassName} onPress={() => handleTabSelect(drawTabId)}>
          <div className="p-8">Draw signature</div>
          <SelectionIndicator className={getSelectionIndicatorClassName} />
        </Tab>
        <Tab id={typeTabId} className={getTabClassName} onPress={() => handleTabSelect(typeTabId)}>
          <div className="p-8">Type signature</div>
          <SelectionIndicator className={getSelectionIndicatorClassName} />
        </Tab>
      </TabList>

      <TabPanel id={drawTabId}>
        <SignatureInputDraw
          value={drawnSignature.value}
          signedAt={drawnSignature.signedAt}
          onChange={handleDrawnSignatureChange}
          isError={isError}
        />
      </TabPanel>

      <TabPanel id={typeTabId}>
        <SignatureInputType
          signedAt={typedSignature.signedAt}
          nameAsSignature={typedSignature.nameAsSignature}
          onChange={handleTypedSignatureChange}
        />
      </TabPanel>
    </Tabs>
  );
}
